import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mail } from "lucide-react";

import AuthLayout from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { safeNext } from "@/auth/loginUrl";
import { errorMessage } from "@/lib/errors";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const token = params.get("token");
  const next = safeNext(params.get("next"), "/CompleteProfile");
  const [state, setState] = useState(token ? "verifying" : "sent");
  const [error, setError] = useState("");
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.auth
      .verify(token)
      .then(async () => {
        await refresh();
        setState("done");
      })
      .catch((err) => {
        setError(errorMessage(err, "That verification link is no longer valid."));
        setState("error");
      });
  }, [token, refresh]);

  const resend = async () => {
    try {
      await api.auth.resendVerification();
      setResent(true);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  if (state === "verifying") {
    return (
      <AuthLayout title="Verifying your email">
        <div className="flex justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
        </div>
      </AuthLayout>
    );
  }

  if (state === "done") {
    return (
      <AuthLayout title="Email verified" subtitle="You're all set to buy tickets.">
        <div className="text-center space-y-4">
          <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
          <Button onClick={() => navigate(user && !user.profile_complete ? "/CompleteProfile" : next)} className="w-full bg-indigo-600 hover:bg-indigo-700">
            Continue
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (state === "error") {
    return (
      <AuthLayout title="Link expired" subtitle={error}>
        <div className="space-y-3 text-center">
          {user ? (
            <Button onClick={resend} className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={resent}>
              {resent ? "Sent — check your inbox" : "Send a new link"}
            </Button>
          ) : (
            <p className="text-sm text-gray-600">
              <Link to="/Login" className="text-indigo-600 font-medium hover:underline">Sign in</Link> and we'll send a fresh link.
            </p>
          )}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Check your inbox" subtitle={`We sent a verification link to ${user?.email ?? params.get("email") ?? "your email"}.`}>
      <div className="text-center space-y-4">
        <Mail className="h-12 w-12 text-indigo-400 mx-auto" />
        <p className="text-sm text-gray-600">Click the link in that email to verify your address. You can browse events in the meantime.</p>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex flex-col gap-2">
          <Button variant="outline" onClick={resend} disabled={resent || !user}>
            {resent ? "Sent again — check your inbox" : "Resend email"}
          </Button>
          <Button onClick={() => navigate("/Events")} className="bg-indigo-600 hover:bg-indigo-700">
            Browse Events
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}
